// Utility Functions

// Date formatting
export const formatDate = (date, format = 'short') => {
    const d = new Date(date);

    if (format === 'short') {
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }

    if (format === 'long') {
        return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    if (format === 'time') {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }

    if (format === 'datetime') {
        return `${formatDate(d, 'short')} at ${formatDate(d, 'time')}`;
    }

    return d.toLocaleDateString();
};

// Number formatting
export const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
};

// Currency formatting
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

// Percentage formatting
export const formatPercent = (value, decimals = 1) => {
    return `${value.toFixed(decimals)}%`;
};

// Metric abbreviation (1000 -> 1K, 1000000 -> 1M)
export const abbreviateNumber = (num) => {
    if (num >= 1000000000) {
        return (num / 1000000000).toFixed(1) + 'B';
    }
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
};

// Calculate percentage change
export const calculateChange = (current, previous) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous) * 100;
};

// Truncate text
export const truncate = (str, length = 100) => {
    if (str.length <= length) return str;
    return str.substring(0, length) + '...';
};

// Generate random ID
export const generateId = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

// Debounce function
export const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

// Get initials from name
export const getInitials = (name) => {
    return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
};

// Color palette generator
export const generateColorPalette = () => {
    const hue = Math.floor(Math.random() * 360);
    return {
        primary: `hsl(${hue}, 70%, 50%)`,
        light: `hsl(${hue}, 70%, 70%)`,
        dark: `hsl(${hue}, 70%, 30%)`,
    };
};

// Platform icon mapper
export const getPlatformIcon = (platform) => {
    const icons = {
        facebook: '📘',
        instagram: '📸',
        linkedin: '💼',
        twitter: '🐦',
        tiktok: '🎵',
        google: '🔍',
    };
    return icons[platform.toLowerCase()] || '📱';
};

// Status color mapper
export const getStatusColor = (status) => {
    const colors = {
        scheduled: 'info',
        posted: 'success',
        failed: 'error',
        draft: 'neutral',
        pending: 'warning',
    };
    return colors[status.toLowerCase()] || 'neutral';
};

// Calculate ROAS
export const calculateROAS = (revenue, spend) => {
    if (spend === 0) return 0;
    return revenue / spend;
};

// Calculate CTR
export const calculateCTR = (clicks, impressions) => {
    if (impressions === 0) return 0;
    return (clicks / impressions) * 100;
};

// Calculate Conversion Rate
export const calculateConversionRate = (conversions, clicks) => {
    if (clicks === 0) return 0;
    return (conversions / clicks) * 100;
};

// Time ago formatter
export const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' years ago';

    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' months ago';

    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' days ago';

    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' hours ago';

    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' minutes ago';

    return 'just now';
};

// Date range presets for campaign filtering
export const DATE_RANGE_PRESETS = {
    TODAY: 'today',
    YESTERDAY: 'yesterday',
    LAST_7_DAYS: 'last_7_days',
    LAST_14_DAYS: 'last_14_days',
    LAST_30_DAYS: 'last_30_days',
    THIS_WEEK: 'this_week',
    LAST_WEEK: 'last_week',
    THIS_MONTH: 'this_month',
    LAST_MONTH: 'last_month',
    THIS_QUARTER: 'this_quarter',
    LAST_QUARTER: 'last_quarter',
    THIS_YEAR: 'this_year',
    LAST_YEAR: 'last_year',
    ALL_TIME: 'all_time'
};

// Display labels for date range presets
export const DATE_RANGE_LABELS = {
    [DATE_RANGE_PRESETS.TODAY]: 'Today',
    [DATE_RANGE_PRESETS.YESTERDAY]: 'Yesterday',
    [DATE_RANGE_PRESETS.LAST_7_DAYS]: 'Last 7 Days',
    [DATE_RANGE_PRESETS.LAST_14_DAYS]: 'Last 14 Days',
    [DATE_RANGE_PRESETS.LAST_30_DAYS]: 'Last 30 Days',
    [DATE_RANGE_PRESETS.THIS_WEEK]: 'Week to Date',
    [DATE_RANGE_PRESETS.LAST_WEEK]: 'Last Week',
    [DATE_RANGE_PRESETS.THIS_MONTH]: 'Month to Date',
    [DATE_RANGE_PRESETS.LAST_MONTH]: 'Last Month',
    [DATE_RANGE_PRESETS.THIS_QUARTER]: 'Quarter to Date',
    [DATE_RANGE_PRESETS.LAST_QUARTER]: 'Last Quarter',
    [DATE_RANGE_PRESETS.THIS_YEAR]: 'Year to Date',
    [DATE_RANGE_PRESETS.LAST_YEAR]: 'Last Year',
    [DATE_RANGE_PRESETS.ALL_TIME]: 'All Time'
};

// Get quarter start month (0-indexed: 0=Jan, 3=Apr, 6=Jul, 9=Oct)
const getQuarterStartMonth = (month) => Math.floor(month / 3) * 3;

// Calculate date range based on preset
export const getDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    switch (preset) {
        case DATE_RANGE_PRESETS.TODAY: {
            return {
                startDate: formatDate(today),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.YESTERDAY: {
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            return {
                startDate: formatDate(yesterday),
                endDate: formatDate(yesterday),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_7_DAYS: {
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 6);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_14_DAYS: {
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 13);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_30_DAYS: {
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 29);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.THIS_WEEK: {
            // Week starts on Sunday
            const dayOfWeek = today.getDay();
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - dayOfWeek);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_WEEK: {
            const dayOfWeek = today.getDay();
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() - dayOfWeek - 1);
            const startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - 6);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.THIS_MONTH: {
            const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_MONTH: {
            const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const endDate = new Date(today.getFullYear(), today.getMonth(), 0);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.THIS_QUARTER: {
            const quarterStart = getQuarterStartMonth(today.getMonth());
            const startDate = new Date(today.getFullYear(), quarterStart, 1);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_QUARTER: {
            const currentQuarterStart = getQuarterStartMonth(today.getMonth());
            const lastQuarterStart = currentQuarterStart - 3;
            const year = lastQuarterStart < 0 ? today.getFullYear() - 1 : today.getFullYear();
            const startMonth = lastQuarterStart < 0 ? 9 : lastQuarterStart; // Q4 of prev year if needed
            const startDate = new Date(year, startMonth, 1);
            const endDate = new Date(today.getFullYear(), currentQuarterStart, 0);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.THIS_YEAR: {
            const startDate = new Date(today.getFullYear(), 0, 1);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.LAST_YEAR: {
            const startDate = new Date(today.getFullYear() - 1, 0, 1);
            const endDate = new Date(today.getFullYear() - 1, 11, 31);
            return {
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                label: DATE_RANGE_LABELS[preset]
            };
        }

        case DATE_RANGE_PRESETS.ALL_TIME:
        default: {
            // Use a far past date for "all time"
            return {
                startDate: '2020-01-01',
                endDate: formatDate(today),
                label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.ALL_TIME]
            };
        }
    }
};

// Get formatted date range description
export const getDateRangeDescription = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (startDate === endDate) {
        return formatDate(start, 'short');
    }

    return `${formatDate(start, 'short')} - ${formatDate(end, 'short')}`;
};
